import { useState, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft } from "lucide-react";
import { searchIndex, SearchEntry } from "../data/searchIndex";

const productLabel: Record<string, string> = {
  flows: "Flows",
  admin: "Admin",
  chat: "Chat",
};

interface DocSearchProps {
  /** Scopes search to a single product. Omit for global search. */
  scope?: string;
  placeholder?: string;
  className?: string;
}

export function DocSearch({ scope, placeholder, className }: DocSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const titleId = useId();
  const statusId = useId();
  const navigate = useNavigate();

  // ⌘K / Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 10);
      setQuery("");
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const container = dialogRef.current;
    const previouslyFocused = document.activeElement;
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const focusable = Array.from(container.querySelectorAll<HTMLElement>(selector));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  const pool = scope ? searchIndex.filter((e) => e.product === scope) : searchIndex;

  const results: SearchEntry[] = query.trim()
    ? pool.filter((e) => {
        const q = query.toLowerCase();
        return (
          e.title.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.section.toLowerCase().includes(q)
        );
      })
    : pool.slice(0, 10);

  // Group by "Product / Section" (global) or just "Section" (scoped)
  const grouped: Record<string, SearchEntry[]> = {};
  for (const r of results) {
    const key = scope ? r.section : `${productLabel[r.product] ?? r.product} — ${r.section}`;
    (grouped[key] ??= []).push(r);
  }

  function go(href: string) {
    setOpen(false);
    navigate(href);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && results[highlighted]) {
      go(results[highlighted].href);
    }
  }

  const ph =
    placeholder ?? (scope ? `Search ${productLabel[scope] ?? scope}…` : "Search the docs…");

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex items-center gap-2 text-sm text-text-faint border border-border rounded-lg px-3 py-2 min-h-11 cursor-pointer hover:border-primary-border bg-bg-secondary transition-colors w-full ${className ?? ""}`}
      >
        <Search size={14} className="shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left truncate">{ph}</span>
        <kbd className="text-xs bg-bg-elevated border border-border rounded px-1.5 py-0.5 font-mono shrink-0">
          ⌘K
        </kbd>
      </button>

      {/* Modal — rendered via portal so sticky/transform parents don't clip it */}
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4"
            onClick={() => setOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              className="relative w-full max-w-xl bg-bg border border-border rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id={titleId} className="sr-only">
                Search documentation
              </h2>
              {/* Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <Search size={16} className="text-text-faint shrink-0" aria-hidden="true" />
                <label htmlFor={inputId} className="sr-only">
                  {ph}
                </label>
                <input
                  id={inputId}
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlighted(0);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={ph}
                  aria-describedby={statusId}
                  className="flex-1 bg-transparent text-sm text-text placeholder:text-text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs bg-bg-secondary border border-border rounded px-1.5 py-0.5 font-mono text-text-faint shrink-0 min-h-6 min-w-6"
                >
                  Esc
                  <span className="sr-only">Close search</span>
                </button>
              </div>

              {/* Results */}
              <div
                id={statusId}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
              >
                {query.trim() ? `${results.length} result${results.length === 1 ? "" : "s"}` : ""}
              </div>
              <div className="max-h-[58vh] overflow-y-auto py-2" aria-label="Search results">
                {results.length === 0 ? (
                  <p className="px-4 py-10 text-sm text-text-faint text-center">
                    No results for &ldquo;{query}&rdquo;
                  </p>
                ) : (
                  Object.entries(grouped).map(([section, items]) => (
                    <div key={section}>
                      <p className="px-4 pt-3 pb-1 text-[10px] font-medium tracking-widest uppercase text-text-faint">
                        {section}
                      </p>
                      {items.map((item) => {
                        const idx = results.indexOf(item);
                        return (
                          <button
                            key={item.href}
                            type="button"
                            aria-current={idx === highlighted ? "true" : undefined}
                            onClick={() => go(item.href)}
                            onMouseEnter={() => setHighlighted(idx)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                              idx === highlighted ? "bg-bg-elevated" : "hover:bg-bg-hover"
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-text truncate">{item.title}</p>
                              <p className="text-xs text-text-muted truncate">{item.description}</p>
                            </div>
                            {idx === highlighted && (
                              <CornerDownLeft size={13} className="text-text-faint shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>

              {/* Footer hints */}
              <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border text-[10px] text-text-faint">
                <span className="flex items-center gap-1">
                  <CornerDownLeft size={10} /> select
                </span>
                <span>↑↓ navigate</span>
                <span>esc close</span>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
